/**
 * ApprovalsPage - Admin ve yöneticiler için birleşik onay kutusu.
 * İzin, fazla mesai, saha modu, saha aktivitesi ve talepleri tek listede gösterir;
 * onaylama / reddetme işlemleri buradan yapılır.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Avatar, Button, Input, Modal, Popconfirm, Segmented, Spin, Tag, Tooltip, message } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/tr';
import { useTranslation } from 'react-i18next';
import { all_routes } from '../../../router/all_routes';
import approvalsService from '../../../services/approvals/approvals.service';
import FileService from '../../../services/file/file.service';
import {
  APPROVAL_TYPE_LABELS,
  APPROVAL_TYPES,
  ApprovalDetail,
  ApprovalItem,
  ApprovalType,
} from '../../../types/approvals.types';
import { handleApiError } from '../../../utils/errorHandler';
import { getFileUrl } from '../../../utils/file.utils';
import ApprovalFlowTags from '../../shared/approval/ApprovalFlowTags';
import { approvalCountsStore } from '../../../hooks/useApprovalCounts';

dayjs.extend(relativeTime);

type FilterKey = 'all' | ApprovalType;

const TYPE_COLORS: Record<ApprovalType, string> = {
  leave: 'blue',
  overtime: 'purple',
  field_mode: 'cyan',
  field_activity: 'geekblue',
  ticket: 'orange',
};

const TYPE_ICONS: Record<ApprovalType, string> = {
  leave: 'ti-calendar',
  overtime: 'ti-clock',
  field_mode: 'ti-map-pin',
  field_activity: 'ti-clipboard-check',
  ticket: 'ti-ticket',
};

const formatDetailValue = (detail: ApprovalDetail): string => {
  if (detail.value === null || detail.value === undefined || detail.value === '') return '-';
  if (detail.kind === 'date') return dayjs(detail.value).format('DD.MM.YYYY');
  if (detail.kind === 'datetime') return dayjs(detail.value).format('DD.MM.YYYY HH:mm');
  return String(detail.value);
};

const getRequesterAvatar = (requester: ApprovalItem['requester']) => {
  if (!requester) return undefined;
  if (requester.profilePicture && requester.profilePicture.trim() !== '') return getFileUrl(requester.profilePicture);
  if (requester.gender === 'male') return '/assets/img/man.png';
  if (requester.gender === 'female') return '/assets/img/woman.png';
  return undefined;
};

const getRequesterName = (requester: ApprovalItem['requester']) =>
  requester ? `${requester.firstName} ${requester.lastName}`.trim() : 'Bilinmeyen Kullanıcı';

/** Backend karar hataları { code, error: { message, description } } formatında döner */
const showDecisionError = (error: any, t: (key: string) => string) => {
  const apiError = error?.response?.data?.error;
  const text = apiError?.message || apiError?.description;
  if (text) {
    message.error(text);
    return;
  }
  handleApiError(error, t);
};

interface ApprovalCardProps {
  item: ApprovalItem;
  deciding: boolean;
  onApprove: (item: ApprovalItem) => void;
  onReject: (item: ApprovalItem) => void;
}

const ApprovalCard: React.FC<ApprovalCardProps> = ({ item, deciding, onApprove, onReject }) => {
  const avatarSrc = getRequesterAvatar(item.requester);
  const subtitle = [item.requester?.title, item.requester?.departmentName].filter(Boolean).join(' · ');
  const initials = item.requester
    ? `${item.requester.firstName?.[0] ?? ''}${item.requester.lastName?.[0] ?? ''}`
    : '?';

  return (
    <div className="card border rounded-lg mb-0 h-100">
      <div className="card-body d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
          <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
            <Avatar
              size={48}
              src={avatarSrc}
              icon={!avatarSrc && !item.requester ? <UserOutlined /> : undefined}
              style={{ backgroundColor: !avatarSrc ? '#1890ff' : undefined, fontSize: '18px', flexShrink: 0 }}
            >
              {!avatarSrc && item.requester ? initials : null}
            </Avatar>
            <div style={{ minWidth: 0 }}>
              <h5 className="mb-1 fw-semibold text-truncate">{getRequesterName(item.requester)}</h5>
              <p className="text-muted mb-0 text-truncate" style={{ fontSize: '0.875rem' }}>
                {subtitle || 'Departman Yok'}
              </p>
            </div>
          </div>
          <div className="text-end flex-shrink-0">
            <Tag color={TYPE_COLORS[item.type]} className="me-0">
              <i className={`ti ${TYPE_ICONS[item.type]} me-1`} />
              {APPROVAL_TYPE_LABELS[item.type] ?? item.type}
            </Tag>
            <Tooltip title={dayjs(item.createdAt).format('DD.MM.YYYY HH:mm')}>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                {dayjs(item.createdAt).locale('tr').fromNow()}
              </div>
            </Tooltip>
          </div>
        </div>

        <h6 className="fw-semibold mb-2">{item.title}</h6>

        {item.details.length > 0 && (
          <div className="row g-2 mb-2">
            {item.details.map((detail, index) => (
              <div key={`${detail.label}-${index}`} className="col-sm-6" style={{ fontSize: '0.875rem' }}>
                <span className="text-muted d-block">{detail.label}</span>
                <span className="fw-medium" style={{ whiteSpace: 'pre-line' }}>
                  {formatDetailValue(detail)}
                </span>
              </div>
            ))}
          </div>
        )}

        {item.description && (
          <div className="p-2 rounded bg-light mb-2" style={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
            {item.description}
          </div>
        )}

        {item.approvalFlow.length > 0 && (
          <div className="mb-2">
            <span className="text-muted d-block mb-1" style={{ fontSize: '0.875rem' }}>Onay Akışı:</span>
            {/* ApprovalFlowTags approverId'yi yalnızca key için kullanır; inbox yanıtında bulunmadığından 0 verilir */}
            <ApprovalFlowTags steps={item.approvalFlow.map((step) => ({ ...step, approverId: 0 }))} />
          </div>
        )}

        {item.documentUrl && (
          <div className="mb-2">
            <Button
              size="small"
              icon={<i className="ti ti-file-download me-1" />}
              onClick={async () => {
                try {
                  await FileService.getInstance().viewFileAsBlob(item.documentUrl as string);
                } catch {
                  message.error('Belge görüntülenirken hata oluştu');
                }
              }}
            >
              Belgeyi Görüntüle
            </Button>
          </div>
        )}

        <div className="d-flex gap-2 mt-auto pt-2">
          <Popconfirm
            title="Talebi onayla"
            description="Bu talebi onaylamak istediğinize emin misiniz?"
            okText="Onayla"
            cancelText="Vazgeç"
            onConfirm={() => onApprove(item)}
            disabled={deciding}
          >
            <button
              type="button"
              disabled={deciding}
              className="btn btn-success flex-fill d-flex align-items-center justify-content-center gap-2"
            >
              <i className="ti ti-check" />
              Onayla
            </button>
          </Popconfirm>
          <button
            type="button"
            disabled={deciding}
            onClick={() => onReject(item)}
            className="btn btn-danger flex-fill d-flex align-items-center justify-content-center gap-2"
          >
            <i className="ti ti-x" />
            Reddet
          </button>
        </div>
      </div>
    </div>
  );
};

const ApprovalsPage: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;

  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [counts, setCounts] = useState<Partial<Record<ApprovalType, number>>>({});
  const [total, setTotal] = useState<number>(0);
  const [failedTypes, setFailedTypes] = useState<ApprovalType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState<string>('');

  const [decidingKey, setDecidingKey] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ApprovalItem | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');

  const lastFetchRef = useRef<number>(0);

  const applyCounts = (next: { counts?: Record<ApprovalType, number>; total?: number; failedTypes?: ApprovalType[] }) => {
    const nextCounts = next.counts ?? {};
    const nextTotal = next.total ?? 0;
    setCounts(nextCounts);
    setTotal(nextTotal);
    setFailedTypes(next.failedTypes ?? []);
    approvalCountsStore.set({ total: nextTotal, counts: nextCounts });
  };

  const fetchPending = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      lastFetchRef.current = Date.now();
      try {
        const response = await approvalsService.getPendingApprovals();
        setItems(response?.items ?? []);
        applyCounts(response ?? {});
      } catch (error: any) {
        if (!silent) handleApiError(error, t);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [t]
  );

  const refreshCounts = async () => {
    try {
      const response = await approvalsService.getApprovalCounts();
      applyCounts(response ?? {});
    } catch {
      // sayaç yenilenemezse sessiz geç
    }
  };

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // Pencere tekrar odaklandığında listeyi sessizce yenile
  useEffect(() => {
    const handleFocus = () => {
      if (Date.now() - lastFetchRef.current > 15000) {
        fetchPending(true);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchPending]);

  const decide = async (item: ApprovalItem, action: 'approve' | 'reject', reason?: string) => {
    setDecidingKey(item.key);
    try {
      const response = await approvalsService.decide(item.type, item.id, {
        action,
        ...(action === 'reject' && reason ? { reason } : {}),
      });
      message.success(response?.message || (action === 'approve' ? 'Talep onaylandı' : 'Talep reddedildi'));
      setItems((prev) => prev.filter((i) => i.key !== item.key));
      setRejectTarget(null);
      setRejectReason('');
      refreshCounts();
    } catch (error: any) {
      showDecisionError(error, t);
      // Talep başka biri tarafından karara bağlanmış olabilir; listeyi senkronla
      fetchPending(true);
    } finally {
      setDecidingKey(null);
    }
  };

  const visibleItems = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    return items.filter((item) => {
      if (activeFilter !== 'all' && item.type !== activeFilter) return false;
      if (!term) return true;
      return getRequesterName(item.requester).toLocaleLowerCase('tr-TR').includes(term);
    });
  }, [items, activeFilter, search]);

  const filterOptions = [
    { label: `Tümü (${total})`, value: 'all' },
    ...APPROVAL_TYPES.map((type) => ({
      label: `${APPROVAL_TYPE_LABELS[type]} (${counts[type] ?? 0})`,
      value: type,
    })),
  ];

  const rejectMode = rejectTarget?.rejectReason ?? 'none';
  const rejectDisabled = rejectMode === 'required' && rejectReason.trim().length === 0;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Onaylar</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Yönetim</li>
                <li className="breadcrumb-item active">Onaylar</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <Button icon={<i className="ti ti-reload me-2" />} onClick={() => fetchPending()} loading={loading}>
              Yenile
            </Button>
          </div>
        </div>

        {failedTypes.length > 0 && (
          <Alert
            type="warning"
            showIcon
            className="mb-3"
            message={`Bazı talep türleri yüklenemedi: ${failedTypes
              .map((type) => APPROVAL_TYPE_LABELS[type] ?? type)
              .join(', ')}`}
          />
        )}

        <div className="card">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Segmented
                  options={filterOptions}
                  value={activeFilter}
                  onChange={(value) => setActiveFilter(value as FilterKey)}
                />
              </div>
              <Input
                placeholder="Çalışan adı ara..."
                prefix={<SearchOutlined />}
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 260, maxWidth: '100%' }}
              />
            </div>
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <Spin size="large" />
            </div>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="mb-3">
                <i
                  className={`ti ${items.length === 0 ? 'ti-circle-check' : 'ti-filter-off'}`}
                  style={{ fontSize: '64px', color: items.length === 0 ? '#52c41a' : '#d9d9d9' }}
                />
              </div>
              <h5 className="mb-2">
                {items.length === 0 ? 'Onay bekleyen talep yok' : 'Filtreye uygun talep bulunamadı'}
              </h5>
              <p className="text-muted mb-0">
                {items.length === 0
                  ? 'Harika! Tüm talepler karara bağlanmış. Yeni talepler geldiğinde burada görüntülenecek.'
                  : 'Farklı bir tür seçmeyi veya aramayı temizlemeyi deneyin.'}
              </p>
            </div>
          </div>
        ) : (
          <Spin spinning={loading}>
            <div className="row g-3">
              {visibleItems.map((item) => (
                <div key={item.key} className="col-xxl-4 col-xl-6 col-md-6 col-sm-12">
                  <ApprovalCard
                    item={item}
                    deciding={decidingKey === item.key}
                    onApprove={(target) => decide(target, 'approve')}
                    onReject={(target) => {
                      setRejectReason('');
                      setRejectTarget(target);
                    }}
                  />
                </div>
              ))}
            </div>
          </Spin>
        )}

        <Modal
          title="Talebi Reddet"
          open={!!rejectTarget}
          okText="Reddet"
          cancelText="Vazgeç"
          okButtonProps={{ danger: true, disabled: rejectDisabled }}
          confirmLoading={!!rejectTarget && decidingKey === rejectTarget.key}
          onOk={() => {
            if (!rejectTarget || rejectDisabled) return;
            decide(rejectTarget, 'reject', rejectReason.trim() || undefined);
          }}
          onCancel={() => {
            setRejectTarget(null);
            setRejectReason('');
          }}
          destroyOnClose
        >
          {rejectTarget && (
            <div>
              <p className="mb-3">
                <strong>{getRequesterName(rejectTarget.requester)}</strong> — {rejectTarget.title}
              </p>
              {rejectMode === 'none' ? (
                <p className="text-muted mb-0">Bu talebi reddetmek istediğinize emin misiniz?</p>
              ) : (
                <>
                  <label className="form-label">
                    Red nedeni
                    {rejectMode === 'required' ? <span className="text-danger"> *</span> : ' (opsiyonel)'}
                  </label>
                  <Input.TextArea
                    rows={4}
                    maxLength={500}
                    showCount
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder={
                      rejectMode === 'required' ? 'Red nedenini yazın...' : 'İsterseniz red nedenini yazabilirsiniz...'
                    }
                  />
                </>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ApprovalsPage;
